import { useGetCompetitorStats } from '@reelplicate/api-client/competitors';
import type { CompetitorAccountStatsDto } from '@reelplicate/api-client/model';
import { Badge, Skeleton, Stack } from '@reelplicate/ui';

import { platformName, postedAgo, views } from './format';
import { Sparkline } from './sparkline';

/** "+1.2K" / "−300" / null. */
function signed(change: number | null) {
  if (change === null || change === 0) return null;
  return `${change > 0 ? '+' : '−'}${views(Math.abs(change))}`;
}

/**
 * The tracked accounts at a glance: followers with this month's change and
 * trend, what their posts usually get, and how many beat that twice over.
 */
export function AccountsSection({
  workspaceId,
  now,
}: {
  workspaceId: string;
  now: number;
}) {
  const { data } = useGetCompetitorStats(
    workspaceId,
    { days: 30 },
    { query: { enabled: Boolean(workspaceId) } },
  );
  if (!data) return <Skeleton className="h-32" shape="rectangle" />;
  if (data.items.length === 0) return null;
  return (
    <section aria-labelledby="accounts-heading" className="flex flex-col gap-3">
      <h2 id="accounts-heading" className="text-heading-h4 text-text-primary">
        Their accounts
      </h2>
      <ul className="bg-bg-surface border-border-subtle divide-border-subtle divide-y rounded-xl border">
        {data.items.map((account) => (
          <AccountRow
            key={`${account.platform}:${account.handle}`}
            account={account}
            now={now}
          />
        ))}
      </ul>
    </section>
  );
}

function AccountRow({
  account,
  now,
}: {
  account: CompetitorAccountStatsDto;
  now: number;
}) {
  const change = signed(account.followersChange);
  return (
    <li className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
      <Stack gap={0.5} className="min-w-40 flex-1">
        <p className="text-body-md-strong text-text-primary">@{account.handle}</p>
        <p className="text-caption text-text-secondary">
          {[account.label, platformName(account.platform)].join(' · ')}
        </p>
      </Stack>
      {account.status === 'NOT_FOUND' ? (
        <Badge tone="danger">Not found - check the handle</Badge>
      ) : !account.lastSyncedAt ? (
        <Badge>Reading their posts…</Badge>
      ) : (
        <>
          <Stack gap={0.5} className="w-28">
            <p className="text-body-md-strong text-text-primary">
              {views(account.followers)}
              <span className="sr-only"> followers</span>
            </p>
            <p className="text-caption text-text-secondary">
              {change ? `${change} this month` : 'followers'}
            </p>
          </Stack>
          <Sparkline points={account.series} label="Followers, last 30 days" />
          <Stack gap={0.5} className="w-32">
            <p className="text-body-sm text-text-primary">
              usually {views(account.medianViews)} views
            </p>
            <p className="text-caption text-text-secondary">
              {account.winners === 0
                ? 'no winners this month'
                : `${account.winners} ${account.winners === 1 ? 'winner' : 'winners'} this month`}
            </p>
          </Stack>
          <p className="text-caption text-text-tertiary w-24 text-right">
            checked {postedAgo(account.lastSyncedAt, now)}
          </p>
        </>
      )}
    </li>
  );
}
